import React from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { SparkleIcon, XIcon } from "@phosphor-icons/react";

const SEEN_KEY = "bidorah_seen_release_version";

/**
 * "What's new in this build" — auto-popup modal that shows the most recent
 * active release once per `version` string. Admins manage releases at
 * /admin → "What's new" panel. Tracks dismissal in localStorage so it never
 * re-shows the same release to the same browser/device.
 *
 * Mounted globally inside <App />. Silent until the latest release exists
 * and the user hasn't seen it yet.
 */
export default function WhatsNewModal() {
  const [release, setRelease] = React.useState(null);
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/releases/latest");
        const r = data?.release;
        if (cancelled || !r || !r.version) return;
        const seen = (() => {
          try {
            return window.localStorage.getItem(SEEN_KEY);
          } catch {
            return null;
          }
        })();
        if (seen !== r.version) {
          setRelease(r);
          // small delay so it lands after first paint, not instantly on load
          setTimeout(() => !cancelled && setOpen(true), 600);
        }
      } catch {
        // silent — release feed is non-critical
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const dismiss = () => {
    if (release?.version) {
      try {
        window.localStorage.setItem(SEEN_KEY, release.version);
      } catch {
        /* ignore quota errors */
      }
    }
    setOpen(false);
  };

  if (!open || !release) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-labelledby="whats-new-title"
      data-testid="whats-new-modal"
      onClick={dismiss}
    >
      <div
        className="relative w-full sm:max-w-md mx-0 sm:mx-4 bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header strip */}
        <div className="relative bg-gradient-to-br from-[#2190CC] via-[#1D78AB] to-[#0E3F5C] text-white px-6 pt-5 pb-6">
          <button
            type="button"
            onClick={dismiss}
            aria-label="Close"
            className="absolute top-3 right-3 h-8 w-8 grid place-items-center rounded-full bg-white/15 hover:bg-white/25 transition-colors"
            data-testid="whats-new-close"
          >
            <XIcon size={16} weight="bold" />
          </button>
          <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-wider uppercase bg-amber-400 text-zinc-900 px-2.5 py-1 rounded-full">
            <SparkleIcon size={11} weight="fill" />
            What's new · v{release.version}
          </div>
          <h2
            id="whats-new-title"
            className="mt-3 font-display font-black text-2xl leading-tight"
            data-testid="whats-new-title"
          >
            {release.title}
          </h2>
        </div>

        {release.image_url && (
          <div className="bg-zinc-100">
            <img
              src={release.image_url}
              alt=""
              className="w-full h-auto max-h-64 object-cover"
            />
          </div>
        )}

        <div className="px-6 pt-5 pb-6">
          <div
            className="text-[15px] leading-relaxed text-zinc-700 whitespace-pre-wrap"
            data-testid="whats-new-body"
          >
            {release.body}
          </div>

          <div className="mt-5 flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              onClick={dismiss}
              className="rounded-full"
              data-testid="whats-new-dismiss"
            >
              Got it
            </Button>
            {release.cta_url && (
              <a
                href={release.cta_url}
                target={release.cta_url.startsWith("http") ? "_blank" : undefined}
                rel="noreferrer"
                onClick={dismiss}
                data-testid="whats-new-cta"
              >
                <Button className="bg-[#1D78AB] hover:bg-[#165d85] text-white rounded-full px-5">
                  {release.cta_label || "Check it out"}
                </Button>
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
